import React, { useEffect, useState } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import "./../../CompanysForm.css"
import { clearCEP, clearCNPJ } from "helpers/clearCepAndCNPJValues"
import { post, put } from "helpers/api_helper"
import Grid from "components/Common/Grid"
import VinculosManagement from "./VinculosManagement"
import { getVinculosByEmpresa } from "helpers/vinculo_helper"

// Função para aplicar máscara de CNPJ
const formatCNPJ = (value) => {
  const numbers = value.replace(/\D/g, '')
  return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

// Função para aplicar máscara de CEP
const formatCEP = (value) => {
  const numbers = value.replace(/\D/g, '')
  return numbers.replace(/^(\d{5})(\d{3})$/, '$1-$2')
}

const validationSchema = Yup.object({
  CNPJ: Yup.string()
    .matches(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, "CNPJ deve estar no formato XX.XXX.XXX/XXXX-XX.")
    .required("CNPJ é obrigatório."),
  NomeFantasia: Yup.string().min(2, "Digite ao mínimo 2 caracteres"),
  RazaoSocial: Yup.string().min(2, "Digite ao mínimo 2 caracteres"),
  PalavrasChave: Yup.string(),
  Endereco: Yup.string(),
  CEP: Yup.string()
    .matches(/^\d{5}-\d{3}$/, "CEP deve estar no formato XXXXX-XXX."),
  Cidade: Yup.string().max(100, "Cidade não pode ter mais que 100 caracteres."),
  UF: Yup.string()
    .length(2, "UF deve ter 2 caracteres.")
    .matches(/^[A-Za-z]{2}$/, "UF deve conter apenas 2 letras."),
  Email: Yup.string()
    .email("Email inválido")
    .max(100, "Email não pode ter mais que 100 caracteres."),
  Corretoras: Yup.string(),
  Seguradora: Yup.string(),
  Status: Yup.string(),
  RangeLeads: Yup.string().max(
    100,
    "Range de Leads não pode ter mais que 100 caracteres."
  ),
})

const MonitoredForm = ({
  companySelected,
  setCompanySelected,
  setShowList,
  setCurrentPage,
}) => {
  const [isLoadingCNPJ, setIsLoadingCNPJ] = useState(false)
  const initialValues = {
    CNPJ:
      companySelected !== null && companySelected.CNPJ !== null
        ? formatCNPJ(clearCNPJ(companySelected.CNPJ))
        : "",
    NomeFantasia:
      companySelected !== null && companySelected.NomeFantasia !== null
        ? companySelected.NomeFantasia
        : "",
    RazaoSocial:
      companySelected !== null && companySelected.RazaoSocial !== null
        ? companySelected.RazaoSocial
        : "",
    PalavrasChave:
      companySelected !== null && companySelected.PalavrasChave !== null
        ? companySelected.PalavrasChave
        : "",
    Endereco:
      companySelected !== null && companySelected.Endereco !== null
        ? companySelected.Endereco
        : "",
    CEP:
      companySelected !== null && companySelected.CEP !== null
        ? formatCEP(clearCEP(companySelected.CEP))
        : "",
    Cidade:
      companySelected !== null && companySelected.Cidade !== null
        ? companySelected.Cidade
        : "",
    UF:
      companySelected !== null && companySelected.UF !== null
        ? companySelected.UF
        : "",
    Email:
      companySelected !== null && companySelected.Email !== null
        ? companySelected.Email
        : "",
    Corretoras:
      companySelected !== null && companySelected.Corretoras !== null
        ? companySelected.Corretoras
        : "",
    Seguradora:
      companySelected !== null && companySelected.Seguradora !== null
        ? companySelected.Seguradora
        : "",
    Status:
      companySelected !== null && companySelected.Status !== null
        ? companySelected.Status
        : "Ativo", // Assuming default is "ativo"
    RangeLeads:
      companySelected !== null && companySelected.RangeLeads !== null
        ? companySelected.RangeLeads
        : "",
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      // Limpar máscaras antes de enviar para a API
      const cleanValues = {
        ...values,
        CNPJ: clearCNPJ(values.CNPJ),
        CEP: clearCEP(values.CEP),
        Status: values.Status === "Ativo" ? true : false
      }
      
      if (companySelected === null) {
        handleCreateNewCompany(cleanValues)
      } else {
        handleEditCompany(cleanValues)
      }
    },
  })

  const handleCreateNewCompany = async payload => {
    const url =
      `${process.env.REACT_APP_API_INT_LEADS}/api/empresas-monitoradas`

    try {
      const response = await post(url, payload)
      console.log(response)
      alert("A empresa foi cadastrada com sucesso!")
      setShowList(true)
      setCurrentPage(1)
      return
    } catch (error) {
      if (error.response?.status === 400) {
        alert(error.response.data.message)
        return
      } else {
        alert(
          "Algo de errado aconteceu! Tente mais tarde ou verifique sua internet"
        )
        return
      }
    }
  }

  const handleEditCompany = async payload => {
    const url = `${process.env.REACT_APP_API_INT_LEADS}/api/empresas-monitoradas/${companySelected.Id}`

    try {
      const response = await put(url, payload)
      console.log(response)
      alert("A empresa foi editada com sucesso!")
      handleCancelEdditing()
      setCurrentPage(1)

      return
    } catch (error) {
      if (error.response?.status === 400) {
        alert(error.response.data.message)
        return
      } else {
        alert(
          "Algo de errado aconteceu! Tente mais tarde ou verifique sua internet"
        )
        return
      }
    }
  }

  const handleCancelEdditing = () => {
    setCompanySelected(null)
    setShowList(true)
  }

  // Função para buscar dados do CNPJ na API BrasilAPI
  const searchCNPJ = async cnpj => {
    setIsLoadingCNPJ(true)
    try {
      const response = await fetch(
        `https://brasilapi.com.br/api/cnpj/v1/${cnpj}`
      )
      const data = await response.json()
      if (data && !data.message) {
        formik.setValues({
          ...formik.values,
          RazaoSocial: data.razao_social || "",
          NomeFantasia: data.nome_fantasia || "",
          Endereco: `${data.logradouro || ""}${data.numero ? ", " + data.numero : ""}`.trim(),
          Cidade: data.municipio || "",
          UF: data.uf || "",
          CEP: data.cep ? formatCEP(data.cep) : "",
        })
      } else {
        alert(data.message || "CNPJ não encontrado")
      }
    } catch (error) {
      alert("Erro ao buscar CNPJ. Verifique sua conexão.")
    } finally {
      setIsLoadingCNPJ(false)
    }
  }

  useEffect(() => {
    !companySelected && formik.resetForm({ values: initialValues })
  }, [companySelected])

  // useEffect para buscar vínculos e preencher Seguradora e Corretoras quando empresa é selecionada
  useEffect(() => {
    const carregarVinculos = async () => {
      if (companySelected && companySelected.Id) {
        try {
          const vinculos = await getVinculosByEmpresa(companySelected.Id)
          
          // Extrair nomes das seguradoras dos vínculos
          const seguradoras = vinculos
            .filter(v => v.Seguradora && v.Seguradora.Nome)
            .map(v => v.Seguradora.Nome)
          
          // Extrair nomes das corretoras dos vínculos
          const corretoras = vinculos
            .filter(v => v.Corretora && v.Corretora.Nome)
            .map(v => v.Corretora.Nome)
          
          // Atualizar o formik com os nomes (separados por ";" se houver múltiplos, ou "-" se vazio)
          formik.setFieldValue('Seguradora', seguradoras.length > 0 ? seguradoras.join('; ') : '-')
          formik.setFieldValue('Corretoras', corretoras.length > 0 ? corretoras.join('; ') : '-')
        } catch (error) {
          console.error('Erro ao carregar vínculos:', error)
          // Em caso de erro, mostrar "-"
          formik.setFieldValue('Seguradora', '-')
          formik.setFieldValue('Corretoras', '-')
        }
      }
    }
    
    carregarVinculos()
  }, [companySelected?.Id])

  // useEffect para autopreenchimento quando CNPJ é inserido
  useEffect(() => {
    if (formik.values.CNPJ) {
      const cnpjNumbers = formik.values.CNPJ.replace(/\D/g, '')
      
      // Buscar dados da empresa quando CNPJ estiver completo
      if (cnpjNumbers.length === 14) {
        searchCNPJ(cnpjNumbers)
      }
    }
  }, [formik.values.CNPJ])

  return (
    <div className="companys-form-container">
      <h1 className="h1-form-title">Preencha o formulário abaixo:</h1>
      <span className="span-form-subtitle">Campos obrigatórios *</span>
      {companySelected !== null && (
        <span className="span-form-edditing">
          Atenção! Você está editando a empresa selecionada!
        </span>
      )}
      <form className="form-companys-container" onSubmit={formik.handleSubmit}>
        <Grid>
          <div className="input-and-label">
            <label className="company-label">CNPJ*</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="XX.XXX.XXX/XXXX-XX"
              name="CNPJ"
              value={formik.values.CNPJ}
              onChange={(e) => {
                const formattedValue = formatCNPJ(e.target.value)
                formik.setFieldValue('CNPJ', formattedValue)
              }}
              onBlur={formik.handleBlur}
              maxLength={18}
            />
            {isLoadingCNPJ && (
              <span className="span-form-loading">Buscando dados...</span>
            )}
            {formik.touched.CNPJ && formik.errors.CNPJ ? (
              <span className="span-form-error-companys">
                {formik.errors.CNPJ}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Razão Social</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="Nome receita"
              name="RazaoSocial"
              value={formik.values.RazaoSocial}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.RazaoSocial && formik.errors.RazaoSocial ? (
              <span className="span-form-error-companys">
                {formik.errors.RazaoSocial}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Nome Fantasia</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="Nome receita"
              name="NomeFantasia"
              value={formik.values.NomeFantasia}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.NomeFantasia && formik.errors.NomeFantasia ? (
              <span className="span-form-error-companys">
                {formik.errors.NomeFantasia}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">CEP</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="XXXXX-XXX"
              name="CEP"
              value={formik.values.CEP}
              onChange={(e) => {
                const formattedValue = formatCEP(e.target.value)
                formik.setFieldValue('CEP', formattedValue)
              }}
              onBlur={formik.handleBlur}
              maxLength={9}
            />
            {formik.touched.CEP && formik.errors.CEP ? (
              <span className="span-form-error-companys">
                {formik.errors.CEP}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Cidade</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="Nome Cidade"
              name="Cidade"
              value={formik.values.Cidade}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Cidade && formik.errors.Cidade ? (
              <span className="span-form-error-companys">
                {formik.errors.Cidade}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">UF</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="UF"
              name="UF"
              value={formik.values.UF}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.UF && formik.errors.UF ? (
              <span className="span-form-error-companys">
                {formik.errors.UF}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Endereço</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="Endereço"
              name="Endereco"
              value={formik.values.Endereco}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Endereco && formik.errors.Endereco ? (
              <span className="span-form-error-companys">
                {formik.errors.Endereco}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Seguradora</label>
            <input
              type="text"
              className="company-input-field"
              name="Seguradora"
              value={formik.values.Seguradora}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={companySelected !== null}
              style={companySelected !== null ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
            />
            {companySelected !== null && (
              <span style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Use a seção "Gerenciar Vínculos" abaixo para adicionar/remover seguradoras
              </span>
            )}
            {formik.touched.Seguradora && formik.errors.Seguradora ? (
              <span className="span-form-error-companys">
                {formik.errors.Seguradora}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Corretoras</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="Corretoras"
              name="Corretoras"
              value={formik.values.Corretoras}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={companySelected !== null}
              style={companySelected !== null ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
            />
            {companySelected !== null && (
              <span style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Use a seção "Gerenciar Vínculos" abaixo para adicionar/remover corretoras
              </span>
            )}
          </div>
          <div className="input-and-label">
            <label className="company-label">Status</label>
            <select
              className="company-input-field"
              name="Status"
              value={formik.values.Status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
            {formik.touched.Status && formik.errors.Status ? (
              <span className="span-form-error-companys">
                {formik.errors.Status}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Email</label>
            <input
              type="Email"
              className="company-input-field"
              placeholder="Email"
              name="Email"
              value={formik.values.Email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Email && formik.errors.Email ? (
              <span className="span-form-error-companys">
                {formik.errors.Email}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Leads de alcance</label>
            <input
              type="text"
              className="company-input-field"
              name="RangeLeads"
              value={formik.values.RangeLeads}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.RangeLeads && formik.errors.RangeLeads ? (
              <span className="span-form-error-companys">
                {formik.errors.RangeLeads}
              </span>
            ) : null}
          </div>
          <div className="input-and-label">
            <label className="company-label">Palavras chave</label>
            <input
              type="text"
              className="company-input-field"
              placeholder="Palavra chave"
              name="PalavrasChave"
              value={formik.values.PalavrasChave}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.PalavrasChave && formik.errors.PalavrasChave ? (
              <span className="span-form-error-companys">
                {formik.errors.PalavrasChave}
              </span>
            ) : null}
          </div>
        </Grid>
        <Grid marginTop={32}>
          <button
            className="btn btn-primary w-md "
            type="submit"
            style={{ height: 40, border: "none" }}
          >
            Enviar
          </button>
          {companySelected !== null && (
            <button
              className="btn btn-primary w-md "
              type="button"
              style={{ backgroundColor: "red", height: 40, border: "none" }}
              onClick={handleCancelEdditing}
            >
              Cancelar
            </button>
          )}
        </Grid>
      </form>

      {/* Componente de Vínculos - só aparece quando está editando uma empresa existente */}
      {companySelected !== null && companySelected.Id && (
        <div style={{ marginTop: '32px' }}>
          <VinculosManagement 
            empresaId={companySelected.Id}
            empresaNome={companySelected.NomeFantasia || companySelected.RazaoSocial}
          />
        </div>
      )}
    </div>
  )
}

export default MonitoredForm
